import { useTranslation } from 'react-i18next';
import { Modal } from '../../Modal';
import { ModalContentContainer } from '../../ModalContentContainer';

import styles from './subscriptions.module.scss';
import { FC, useState } from 'react';
import { Button } from '../../Buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum, userRoles } from '../../../shared';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { dailyExerciseApi, serviceApi } from '../../../store/services';
import { showNotification } from '../../../store/reducers';

export interface SubscriptionsDialogProps {
 isOpen: boolean;
 onClose: () => void;
 modalTitle: string;
 subtitle?: string;
}

export const SubscriptionsDialog: FC<SubscriptionsDialogProps> = ({
 isOpen,
 onClose,
 modalTitle,
 subtitle = '',
}) => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const { patientId: queryPatientId } = useParams();
 const currentUser = useAppSelector(
  ({ userReducer }) => userReducer.currentUser,
 );
 const isPatient = currentUser?.role === userRoles.patient || !currentUser;
 const [selectedSubscription, setSelectedSubscription] = useState<
  string | null
 >(null);

 const { data: onlineServices } = serviceApi.useFetchServiceListQuery({
  page: 1,
  limit: 100,
  isOnline: true,
  isRelatedToExercise: true,
  isActive: true,
 });

 const onlineService = onlineServices?.items[0];

 const { data: subscriptionPlansWithPriceForPatient } =
  dailyExerciseApi.useFetchPatientSubscriptionPlansQuery(
   onlineService?._id || '',
   {
    skip: !onlineService?._id || !isPatient,
   },
  );

 const { data: subscriptionPlansWithPriceForStaff } =
  dailyExerciseApi.useFetchPatientSubscriptionPlansByStaffQuery(
   { serviceId: onlineService?._id || '', patientId: queryPatientId || '' },
   {
    skip: !onlineService?._id || isPatient,
   },
  );

 const subscriptionPlansWithPrice = isPatient
  ? subscriptionPlansWithPriceForPatient?.plans
  : subscriptionPlansWithPriceForStaff?.plans;

 const [handleCreateSubscription] =
  dailyExerciseApi.useCreateSubscriptionByPatientMutation();
 const [handleCreateSubscriptionByStaff] =
  dailyExerciseApi.useCreateSubscriptionByStaffMutation();

 const onHandleCreateSubscription = async () => {
  try {
   if (selectedSubscription && onlineService?._id) {
    const res =
     !queryPatientId && isPatient
      ? await handleCreateSubscription({
         serviceId: onlineService._id,
         subscriptionPlanId: selectedSubscription,
        }).unwrap()
      : await handleCreateSubscriptionByStaff({
         patientId: queryPatientId as string,
         serviceId: onlineService._id,
         subscriptionPlanId: selectedSubscription,
        }).unwrap();

    navigate(`${RoutesEnum.admin}${RoutesEnum.subscriptionPayment}`, {
     state: {
      subscriptionId: res?._id,
      totalCost: res?.price,
      currency: res?.currency,
      patientId: queryPatientId || currentUser?._id,
     },
    });
    onClose();
   } else {
    console.warn(t('subscriptions.selectSubscriptionWarning'));
   }
  } catch (e: any) {
   dispatch(
    showNotification({
     type: 'error',
     message: t([
      `alert.errors.${e.data.message}`,
      `alert.errors.${e.data.response?.message}`,
      'alert.somethingWentWrong',
     ]),
    }),
   );
  }
 };

 return (
  <Modal
   isOpen={isOpen}
   onClose={onClose}
   customStyles={styles.SubscriptionsModal}
  >
   <ModalContentContainer
    modalTitle={modalTitle}
    subtitle={subtitle}
    customStyles={styles.SubscriptionsModalContent}
   >
    {subscriptionPlansWithPrice?.length ? (
     subscriptionPlansWithPrice.map((subscription) => (
      <button
       type='button'
       key={subscription._id}
       className={`${styles.SubscriptionButton} ${
        selectedSubscription === subscription._id && styles.selectedSubscription
       }`}
       onClick={() => setSelectedSubscription(subscription._id)}
      >
       <span>
        {subscription.amountOfVisits} {t('subscriptions.sessions')}
       </span>
       <span className={styles.price}>
        {subscription.price}
        <span className={styles.currency}>{subscription.currency}</span>
       </span>
      </button>
     ))
    ) : (
     <span className={styles.no_data}>{t('noSubscriptionPlansAvailable')}</span>
    )}
    <Button
     label={t('buttons.purchaseSubscription')}
     className='small_green'
     customStyle={styles.PurchaseSubscriptionButton}
     onClick={() => onHandleCreateSubscription()}
     disabled={!selectedSubscription}
    />
   </ModalContentContainer>
  </Modal>
 );
};
